import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Tab, Tabs, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate, useParams } from 'react-router-dom';

import GradientCircularProgress from '../../../../../components/common/GradientCircularProgress';
import AccountEventsTable from '../../../../../components/account/AccountEventsTable';
import { useThemeContext } from '../../../../../contexts/BaseThemeContextProvider';
import useBackendApi from '../../../../../hooks/useBackendApi';
import { RequestOptions } from '../../../../../types/httpRequests';
import { AccountEvent, AccountType } from '../../../../../types/account';
import { AccountEventsResponse } from '../../../../../types/api/accountEvents';
import PrimaryBlueButton from '../../../../../components/common/buttons/PrimaryBlueButton';
import BorderedButton from '../../../../../components/common/buttons/BorderedButton';
import { hasAdminOrMemberRole } from './roleUtils';
import { applyCalendarSyncIssue } from './calendarSyncUtils';
import CalendarConnectNotice from './CalendarConnectNotice';
import useCalendarIntegration from './useCalendarIntegration';
import { parseDateTime } from '../../../../../utils/dateTime';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

type RouteParams = {
  org_path_name: string;
  acc_path_name: string;
};

type EventsTab = 'upcoming' | 'past';

export default function AccountEvents(): JSX.Element {
  const { org_path_name, acc_path_name } = useParams<RouteParams>() as RouteParams;
  const navigate = useNavigate();
  const { theme } = useThemeContext();
  const { apiFetchRequest, isLoading } = useBackendApi();

  const [account, setAccount] = useState<AccountType | null>(null);
  const [events, setEvents] = useState<AccountEvent[]>([]);
  const [viewerOrgRoles, setViewerOrgRoles] = useState<string[]>([]);
  const [viewerAccountRoles, setViewerAccountRoles] = useState<string[]>([]);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<Record<number, HTMLElement | null>>({});
  const [deletingEventId, setDeletingEventId] = useState<number | null>(null);
  const [activeEventsTab, setActiveEventsTab] = useState<EventsTab>('upcoming');
  const {
    calendarStatus,
    calendarActionError,
    calendarActionLoading,
    fetchCalendarStatus,
    handleConnectCalendar,
    handleDisconnectCalendar,
    setCalendarActionError,
  } = useCalendarIntegration();
  const fail = useCallback(() => setErrorText('Something went wrong. Please try again.'), []);

  const fetchEventsAndLocations = useCallback(async () => {
    const opts: RequestOptions = {
      endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(acc_path_name)}/events/`,
      method: 'GET',
    };

    try {
      const res = await apiFetchRequest(opts);

      if (!res) {
        fail();
        return;
      }

      const { body, status, isError } = res;
      const typedBody = body as AccountEventsResponse | undefined;

      const unauthorized =
        status === 401 ||
        body?.status_code === 401 ||
        body?.message === 'UNAUTHENTICATED' ||
        body?.message === 'UNAUTHORIZED' ||
        body?.message === 'NO_ACCESS';

      if (unauthorized) {
        navigate('/401.html');
        return;
      }

      if (status === 404 || body?.status_code === 404 || body?.message === 'NOT_FOUND') {
        navigate('/404.html');
        return;
      }

      if (isError || status !== 200) {
        fail();
        return;
      }

      if (typedBody?.account) {
        setAccount(typedBody.account);
        setEvents(Array.isArray(typedBody.events) ? typedBody.events : []);
        setViewerOrgRoles(Array.isArray(typedBody.viewer_org_roles) ? typedBody.viewer_org_roles : []);
        setViewerAccountRoles(Array.isArray(typedBody.viewer_account_roles) ? typedBody.viewer_account_roles : []);
        setErrorText(null);
      } else {
        fail();
      }
    } catch {
      fail();
    }
  }, [acc_path_name, apiFetchRequest, navigate, org_path_name, fail]);

  useEffect(() => {
    fetchEventsAndLocations();
  }, [acc_path_name, fetchEventsAndLocations, org_path_name]);

  const handleCreateEvent = useCallback(() => {
    navigate(`/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(acc_path_name)}/events/new`);
  }, [acc_path_name, navigate, org_path_name]);

  const handleEditEvent = useCallback(
    (event: AccountEvent) => {
      navigate(
        `/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(acc_path_name)}/events/${encodeURIComponent(String(event.acc_event_id))}/edit`,
        {
          state: { event, viewer_org_roles: viewerOrgRoles },
        },
      );
    },
    [acc_path_name, navigate, org_path_name, viewerOrgRoles],
  );

  const handleOpenActionsMenu = useCallback((clickEvent: MouseEvent<HTMLElement>, eventId: number) => {
    clickEvent.stopPropagation();
    const target = clickEvent.currentTarget as HTMLElement;
    setMenuAnchorEl((prev) => ({ ...prev, [eventId]: target }));
  }, []);

  const handleCloseActionsMenu = useCallback((eventId: number) => {
    setMenuAnchorEl((prev) => ({ ...prev, [eventId]: null }));
  }, []);

  const handleDeleteEvent = useCallback(
    async (eventToDelete: AccountEvent) => {
      const eventId = eventToDelete.acc_event_id;
      const confirmed = window.confirm('Are you sure you want to delete this event? This action cannot be undone.');
      if (!confirmed) {
        handleCloseActionsMenu(eventId);
        return;
      }

      setCalendarActionError(null);
      setDeletingEventId(eventId);
      handleCloseActionsMenu(eventId);

      try {
        const response = await apiFetchRequest({
          endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
            acc_path_name,
          )}/events/${encodeURIComponent(String(eventId))}`,
          method: 'DELETE',
        });

        if (!response) {
          fail();
          return;
        }

        const handled = await applyCalendarSyncIssue(response, {
          onMessage: setCalendarActionError,
          onNotConnected: fetchCalendarStatus,
        });
        if (handled) {
          return;
        }

        if (response.isError || (response.status !== 200 && response.status !== 204)) {
          fail();
          return;
        }

        await fetchEventsAndLocations();
      } catch {
        fail();
      } finally {
        setDeletingEventId(null);
      }
    },
    [
      acc_path_name,
      apiFetchRequest,
      fail,
      fetchEventsAndLocations,
      fetchCalendarStatus,
      handleCloseActionsMenu,
      org_path_name,
      setCalendarActionError,
    ],
  );

  const canManageEvents = useMemo(() => {
    if (hasAdminOrMemberRole(viewerOrgRoles)) {
      return true;
    }
    return hasAdminOrMemberRole(viewerAccountRoles);
  }, [viewerAccountRoles, viewerOrgRoles]);

  const splitEventsByTab = useMemo(() => {
    const nowMs = Date.now();
    const upcomingEvents: AccountEvent[] = [];
    const pastEvents: AccountEvent[] = [];

    events.forEach((event) => {
      const endTime = parseDateTime(event.event_end_date);

      if (!endTime || endTime.getTime() >= nowMs) {
        upcomingEvents.push(event);
        return;
      }
      pastEvents.push(event);
    });

    return {
      upcomingEvents,
      pastEvents,
    };
  }, [events]);

  const visibleEvents = activeEventsTab === 'upcoming' ? splitEventsByTab.upcomingEvents : splitEventsByTab.pastEvents;
  const emptyTabMessage = activeEventsTab === 'upcoming' ? 'No upcoming events.' : 'No past events.';

  const renderEventActions = useCallback(
    (event: AccountEvent) => {
      const eventId = event.acc_event_id;

      return (
        <>
          <IconButton
            aria-label="Event actions"
            onClick={(clickEvent) => handleOpenActionsMenu(clickEvent, eventId)}
            size="small"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchorEl[eventId] ?? null}
            open={Boolean(menuAnchorEl[eventId])}
            onClose={() => handleCloseActionsMenu(eventId)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem
              onClick={async (menuItemEvent) => {
                menuItemEvent.stopPropagation();
                handleCloseActionsMenu(eventId);
                await handleEditEvent(event);
              }}
            >
              Edit Event
            </MenuItem>
            <MenuItem
              onClick={(menuItemEvent) => {
                menuItemEvent.stopPropagation();
                handleDeleteEvent(event);
              }}
              disabled={deletingEventId === eventId}
            >
              Delete Event
            </MenuItem>
          </Menu>
        </>
      );
    },
    [deletingEventId, handleCloseActionsMenu, handleDeleteEvent, handleOpenActionsMenu, handleEditEvent, menuAnchorEl],
  );

  if (isLoading && !account && !errorText) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', height: '100vh' }}>
        <GradientCircularProgress />
      </Box>
    );
  }

  if (errorText) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', px: 2 }}>
        <Typography color="error">{errorText}</Typography>
      </Box>
    );
  }

  if (!account) {
    return <></>;
  }

  return (
    <Box
      sx={{
        backgroundColor: theme.layout.defaults.backgroundColor,
        borderRadius: '16px',
        padding: '40px',
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
        {canManageEvents ? (
          <PrimaryBlueButton
            onClick={handleCreateEvent}
            sxProps={{
              padding: '10px 24px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: { xs: '100%', md: '160px' },
            }}
            buttonText="Create Event"
          />
        ) : null}
        <BorderedButton
          onClick={calendarStatus?.connected ? handleDisconnectCalendar : handleConnectCalendar}
          buttonText={calendarStatus?.connected ? 'Disconnect Calendar' : 'Connect Google Calendar'}
          isLoading={calendarActionLoading}
        />
        {calendarStatus?.connected && calendarStatus?.provider_email ? (
          <Typography variant="body2">Connected as {calendarStatus.provider_email}</Typography>
        ) : null}
      </Box>
      <CalendarConnectNotice connected={calendarStatus?.connected} />
      {calendarActionError ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {calendarActionError}
        </Typography>
      ) : null}
      <Typography variant="h6" sx={{ mb: 1 }}>
        Account Events
      </Typography>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeEventsTab}
          onChange={(_, value: EventsTab) => setActiveEventsTab(value)}
          aria-label="Account events tabs"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: 40,
              fontWeight: 500,
            },
          }}
        >
          <Tab label="Upcoming" value="upcoming" />
          <Tab label="Past" value="past" />
        </Tabs>
      </Box>
      {visibleEvents.length ? (
        <AccountEventsTable
          events={visibleEvents}
          getRowKey={(event) => event.acc_event_id ?? event.title ?? `${event.acc_id}-${event.event_start_date}`}
          onRowClick={(event) =>
            navigate(
              `/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
                acc_path_name,
              )}/events/${encodeURIComponent(String(event.acc_event_id))}`,
            )
          }
          renderActions={canManageEvents ? renderEventActions : undefined}
          hideColumns={['Type']}
          showAssignedStaff={hasAdminOrMemberRole(viewerOrgRoles)}
        />
      ) : (
        <Box
          sx={{
            backgroundColor: theme.profile.backgroundColor,
            borderRadius: '16px',
            padding: '40px',
            textAlign: 'center',
          }}
        >
          <Typography variant="h6">{emptyTabMessage}</Typography>
        </Box>
      )}
    </Box>
  );
}
